// Combobox de búsqueda de un recurso FHIR existente del paciente — reemplazo
// shadcn de `ResourceInput` (`@medplum/react`, Mantine) para los formularios
// discretos del Grupo 4 que referencian un recurso ya creado por otro
// formulario (ej. 007/053 → ServiceRequest de origen, 022 → MedicationRequest
// a administrar, 018A → Procedure asociado). Mismo patrón visual que
// ValueSetCombobox.tsx (Popover+Command), pero contra `useSearchResources`
// en vez de `valueSetExpand`.
import { useSearchResources } from '@medplum/react-hooks';
import type { ExtractResource, Reference, ResourceType } from '@medplum/fhirtypes';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { JSX } from 'react';
import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface SelectorRecursoProps<K extends ResourceType> {
  readonly resourceType: K;
  readonly subject: Reference;
  readonly label: string;
  readonly etiqueta: (recurso: ExtractResource<K>) => string;
  readonly value: ExtractResource<K> | undefined;
  readonly onChange: (recurso: ExtractResource<K> | undefined) => void;
  /** Parámetros de búsqueda extra (p. ej. `category`) para no listar recursos de otro tipo de formulario. */
  readonly filtro?: Record<string, string>;
}

export function SelectorRecurso<K extends ResourceType>({ resourceType, subject, label, etiqueta, value, onChange, filtro }: SelectorRecursoProps<K>): JSX.Element {
  const listboxId = useId();
  const [abierto, setAbierto] = useState(false);
  const [recursos] = useSearchResources(resourceType, { subject: subject.reference ?? '', ...filtro, _sort: '-_lastUpdated', _count: 20 });

  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- patrón shadcn Popover+Command: el trigger es un botón real, no un <input>/<select> nativo. */}
          <Button type="button" variant="outline" role="combobox" aria-expanded={abierto} aria-controls={listboxId} className="w-full justify-between font-normal">
            <span className={cn('truncate', !value && 'text-muted-foreground')}>{value ? etiqueta(value) : label}</span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandList id={listboxId}>
              <CommandEmpty>Sin resultados.</CommandEmpty>
              <CommandGroup>
                {(recursos ?? []).map((r) => (
                  <CommandItem
                    key={r.id}
                    value={r.id}
                    onSelect={() => {
                      onChange(r);
                      setAbierto(false);
                    }}
                  >
                    <Check className={cn('mr-2 size-4', value?.id === r.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{etiqueta(r)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
